import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { canvasTexture, stripeTexture } from "./textures.js";

const UNIT_BOX = new THREE.BoxGeometry(1, 1, 1);
const UNIT_CYLINDER = new THREE.CylinderGeometry(1, 1, 1, 8);

function box(group, size, position, material, cast = true, receive = true) {
  const mesh = new THREE.Mesh(UNIT_BOX, material);
  mesh.scale.set(...size);
  mesh.position.set(...position);
  mesh.castShadow = cast;
  mesh.receiveShadow = receive;
  group.add(mesh);
  return mesh;
}

function rodGeometry(from, to, radius = 0.18, segments = 8) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const direction = b.clone().sub(a);
  const center = a.clone().add(b).multiplyScalar(0.5);
  const geometry = new THREE.CylinderGeometry(radius, radius, direction.length(), segments);
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    direction.normalize(),
  );
  geometry.applyMatrix4(new THREE.Matrix4().compose(center, quaternion, new THREE.Vector3(1, 1, 1)));
  return geometry;
}

function mergedRods(lines, material, radius = 0.18) {
  const geometries = lines.map(([from, to, customRadius]) => rodGeometry(from, to, customRadius ?? radius));
  const mesh = new THREE.Mesh(mergeGeometries(geometries, false), material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function instancedBoxes(group, transforms, material, cast = false) {
  const mesh = new THREE.InstancedMesh(UNIT_BOX, material, transforms.length);
  const dummy = new THREE.Object3D();
  transforms.forEach(({ position, scale, rotation = [0, 0, 0] }, index) => {
    dummy.position.set(...position);
    dummy.rotation.set(...rotation);
    dummy.scale.set(...scale);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  });
  mesh.instanceMatrix.needsUpdate = true;
  mesh.castShadow = cast;
  mesh.receiveShadow = true;
  group.add(mesh);
  return mesh;
}

function makePhotoBackdrop(renderer, filename, position, rotationY, size) {
  const url = `${import.meta.env.BASE_URL}assets/photos/${filename}`;
  const texture = new THREE.TextureLoader().load(url, loaded => {
    loaded.colorSpace = THREE.SRGBColorSpace;
    loaded.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  });
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.MeshBasicMaterial({ map: texture, toneMapped: false, side: THREE.DoubleSide });
  const panel = new THREE.Mesh(new THREE.PlaneGeometry(...size), material);
  panel.position.set(...position);
  panel.rotation.y = rotationY;
  panel.receiveShadow = false;
  return panel;
}

function addContextTrees(group) {
  const treePositions = [
    [-49, -53], [-34, -58], [-16, -61], [3, -64], [22, -61], [42, -55],
    [-57, 40], [-59, 22], [59, 34], [61, 12], [59, -20],
  ];
  const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x765238, roughness: 1 });
  const crownMaterial = new THREE.MeshStandardMaterial({ color: 0x557b45, roughness: 1 });
  const trunks = new THREE.InstancedMesh(UNIT_CYLINDER, trunkMaterial, treePositions.length);
  const crowns = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 1), crownMaterial, treePositions.length * 2);
  const dummy = new THREE.Object3D();
  treePositions.forEach(([x, z], index) => {
    const height = 4.5 + (index % 3) * 0.55;
    dummy.position.set(x, height * 0.5, z);
    dummy.scale.set(0.28, height, 0.28);
    dummy.updateMatrix();
    trunks.setMatrixAt(index, dummy.matrix);
    for (let layer = 0; layer < 2; layer += 1) {
      dummy.position.set(x + (layer ? 0.55 : -0.35), height + 1.2 + layer * 0.9, z + (layer ? -0.2 : 0.35));
      dummy.scale.set(2.2 - layer * 0.2, 1.65, 2.2 - layer * 0.2);
      dummy.updateMatrix();
      crowns.setMatrixAt(index * 2 + layer, dummy.matrix);
    }
  });
  trunks.instanceMatrix.needsUpdate = true;
  crowns.instanceMatrix.needsUpdate = true;
  trunks.castShadow = false;
  crowns.castShadow = false;
  group.add(trunks, crowns);
}

export function createOptimizedVenue(renderer) {
  const group = new THREE.Group();
  group.name = "臺北表演藝術中心－實景核對模型";

  const plazaMap = stripeTexture(renderer);
  plazaMap.repeat.set(8, 8);
  const plazaMaterial = new THREE.MeshStandardMaterial({ map: plazaMap, roughness: 0.92 });
  const plaza = new THREE.Mesh(new THREE.PlaneGeometry(118, 120), plazaMaterial);
  plaza.rotation.x = -Math.PI / 2;
  plaza.position.set(0, 0.012, -2);
  plaza.receiveShadow = true;
  group.add(plaza);

  // Keep the site context fixed and rotate only the architectural mass.
  // The Globe Playhouse must face the Jiantan MRT / event-entry side.
  const building = new THREE.Group();
  building.name = "臺北表演藝術中心－建築主體";
  building.rotation.y = -Math.PI / 2;
  building.userData.rotationCorrection = "left-90deg";
  group.add(building);

  const glass = new THREE.MeshPhysicalMaterial({
    color: 0x15232b,
    metalness: 0.42,
    roughness: 0.24,
    clearcoat: 0.25,
    clearcoatRoughness: 0.32,
  });
  const deepGlass = new THREE.MeshStandardMaterial({ color: 0x0c171d, metalness: 0.42, roughness: 0.26 });
  const silver = new THREE.MeshStandardMaterial({ color: 0xb8bdc0, metalness: 0.62, roughness: 0.38 });
  const paleMetal = new THREE.MeshStandardMaterial({ color: 0xd4d5d4, metalness: 0.48, roughness: 0.46 });
  const structural = new THREE.MeshStandardMaterial({ color: 0x69747a, metalness: 0.76, roughness: 0.32 });

  // The real building reads as a lifted dark cube with three theatres inserted into it.
  box(building, [36, 32, 38], [0, 32, -7], glass, true);
  box(building, [35.5, 4.2, 37.5], [0, 18.2, -7], deepGlass, false);
  box(building, [36, 18, 23], [-27, 31, 2], paleMetal, true);
  box(building, [21, 20, 40], [11, 29, 21], silver, true);
  box(building, [20, 19, 22], [7, 31, -32], paleMetal, true);

  const theatrePanelSeams = [];
  for (let x = -43; x <= -11; x += 2.6) theatrePanelSeams.push({ position: [x, 31, 13.53], scale: [0.055, 17.2, 0.05] });
  for (let x = 1.5; x <= 20.5; x += 2.45) theatrePanelSeams.push({ position: [x, 29, 41.03], scale: [0.05, 19.2, 0.05] });
  for (let x = -2; x <= 16; x += 2.5) theatrePanelSeams.push({ position: [x, 31, -20.97], scale: [0.05, 18.2, 0.05] });
  instancedBoxes(building, theatrePanelSeams, structural, false);

  const horizontalFins = [];
  for (const z of [12.25, -26.25]) {
    for (let y = 20; y <= 47; y += 2.35) {
      horizontalFins.push({ position: [0, y, z], scale: [35.6, 0.15, 0.6] });
    }
  }
  instancedBoxes(building, horizontalFins, structural, false);

  const mullions = [];
  for (const z of [12.58, -26.58]) {
    for (let x = -16; x <= 16; x += 4) {
      mullions.push({ position: [x, 33, z], scale: [0.1, 28, 0.16] });
    }
  }
  instancedBoxes(building, mullions, structural, false);

  const globeMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xc2c4c4,
    metalness: 0.66,
    roughness: 0.38,
    clearcoat: 0.28,
  });
  const globe = new THREE.Mesh(new THREE.SphereGeometry(15.7, 36, 24), globeMaterial);
  globe.scale.z = 0.94;
  globe.position.set(25.2, 30.2, -2.2);
  globe.castShadow = true;
  globe.receiveShadow = true;
  building.add(globe);

  const globeLines = new THREE.LineSegments(
    new THREE.WireframeGeometry(new THREE.SphereGeometry(15.82, 14, 9)),
    new THREE.LineBasicMaterial({ color: 0x878d90, transparent: true, opacity: 0.32 }),
  );
  globeLines.scale.z = 0.94;
  globeLines.position.copy(globe.position);
  building.add(globeLines);

  const supportLines = [
    [[-37, 0, 12], [-24, 21, 5], 0.82], [[-17, 0, 15], [-26, 21, 5], 0.82],
    [[19, 0, 13], [19, 20, 1], 0.82], [[34, 0, 14], [26, 20, 1], 0.82],
    [[14, 0, -17], [17, 20, -6], 0.72], [[36, 0, -18], [28, 20, -6], 0.72],
    [[-13, 0, -8], [-13, 16, -8], 0.58], [[0, 0, -8], [0, 16, -8], 0.58],
    [[13, 0, -8], [13, 16, -8], 0.58],
  ];
  building.add(mergedRods(supportLines, structural));

  box(building, [34, 0.75, 35], [0, 48.45, -7], new THREE.MeshStandardMaterial({ color: 0x607d49, roughness: 1 }), false);
  box(building, [37, 0.32, 40], [0, 16.15, -7], structural, false);

  const entranceGlass = new THREE.MeshPhysicalMaterial({ color: 0x89b6c6, metalness: 0.15, roughness: 0.2, transparent: true, opacity: 0.55 });
  const entrancePanels = [];
  for (let x = -12; x <= 12; x += 4) entrancePanels.push({ position: [x, 5.1, 12.35], scale: [3.7, 10.2, 0.08] });
  instancedBoxes(building, entrancePanels, entranceGlass, false);

  const venueSignTexture = canvasTexture({
    title: "臺北表演藝術中心",
    subtitle: "TAIPEI PERFORMING ARTS CENTER",
    background: "#082d57",
    accent: "#ea5a18",
    width: 1024,
    height: 256,
  });
  const venueSign = new THREE.Mesh(
    new THREE.PlaneGeometry(15, 3.75),
    new THREE.MeshBasicMaterial({ map: venueSignTexture, toneMapped: false }),
  );
  venueSign.position.set(-27, 31, 13.56);
  building.add(venueSign);

  // Photo mattes use licensed real-location imagery already documented in the project.
  const rearPhoto = makePhotoBackdrop(renderer, "tpac-city-view-v2.jpg", [0, 20, -78], 0, [92, 42]);
  const sidePhoto = makePhotoBackdrop(renderer, "tpac-globe-street-v2.jpg", [-78, 17, -8], Math.PI / 2, [66, 34]);
  rearPhoto.renderOrder = -2;
  sidePhoto.renderOrder = -2;
  group.add(rearPhoto, sidePhoto);
  addContextTrees(group);

  return group;
}
